/**
 * Command Registry
 *
 * Central registry for command metadata. Commands register here to be
 * discoverable by the command palette and context menu.
 *
 * Note: The actual command execution and keyboard shortcuts are handled by
 * the InputSystem. This registry only holds UI metadata (labels, icons, etc.)
 */

import type { Command, CommandMeta, CommandCategory } from './types';
import type { UINode } from '@rool/renderer';

/**
 * Registered commands with their UI metadata.
 * Key is command ID, value is the metadata.
 */
const commandMetaMap = new Map<string, CommandMeta>();

/**
 * Execute callbacks for commands.
 * Key is command ID, value is the execute function.
 */
const executeMap = new Map<string, () => void | Promise<void>>();

/**
 * Register a command with its metadata and execute function.
 */
export function registerCommand(
	id: string,
	meta: CommandMeta,
	execute: () => void | Promise<void>
): void {
	commandMetaMap.set(id, meta);
	executeMap.set(id, execute);
}

/**
 * Unregister a command.
 */
export function unregisterCommand(id: string): void {
	commandMetaMap.delete(id);
	executeMap.delete(id);
}

/**
 * Get a command by ID.
 */
export function getCommand(id: string): Command | undefined {
	const meta = commandMetaMap.get(id);
	const execute = executeMap.get(id);
	if (!meta || !execute) return undefined;

	return {
		id,
		...meta,
		execute,
	};
}

/**
 * Get all registered commands.
 */
export function getAllCommands(): Command[] {
	const commands: Command[] = [];
	for (const [id, meta] of commandMetaMap) {
		const execute = executeMap.get(id);
		if (execute) {
			commands.push({ id, ...meta, execute });
		}
	}
	return commands;
}

/**
 * Get commands for the command palette.
 * Returns all commands, filtered by dynamic getLabel visibility.
 */
export function getPaletteCommands(selectedNodes: UINode[]): Command[] {
	return getAllCommands().filter((cmd) => {
		if (cmd.getLabel) {
			return cmd.getLabel(selectedNodes) !== false;
		}
		return true;
	});
}

/**
 * Get commands for context menu, filtered by category and selection.
 */
export function getContextMenuCommands(
	category: 'Canvas' | 'Canvas (Selection)',
	selectedNodes: UINode[]
): Array<Command & { displayLabel: string }> {
	return getAllCommands()
		.filter((cmd) => {
			// Filter by category
			if (cmd.category !== category) return false;

			// Filter by context menu visibility
			if (cmd.showInContextMenu === false) return false;

			// Filter by dynamic visibility
			if (cmd.getLabel) {
				return cmd.getLabel(selectedNodes) !== false;
			}

			return true;
		})
		.map((cmd) => ({
			...cmd,
			displayLabel: cmd.getLabel ? (cmd.getLabel(selectedNodes) as string) : cmd.label,
		}));
}

/**
 * Get commands grouped by section for context menu rendering.
 */
export function getContextMenuCommandsBySection(
	category: 'Canvas' | 'Canvas (Selection)',
	selectedNodes: UINode[]
): Map<string | undefined, Array<Command & { displayLabel: string }>> {
	const commands = getContextMenuCommands(category, selectedNodes);
	const sections = new Map<string | undefined, Array<Command & { displayLabel: string }>>();

	for (const cmd of commands) {
		const section = cmd.section;
		if (!sections.has(section)) {
			sections.set(section, []);
		}
		sections.get(section)!.push(cmd);
	}

	return sections;
}

/**
 * Execute a command by ID.
 * Returns true if the command was found and executed.
 */
export function executeCommand(id: string): boolean {
	const execute = executeMap.get(id);
	if (!execute) return false;

	execute();
	return true;
}

/**
 * Clear all registered commands.
 * Useful for testing or reinitialization.
 */
export function clearRegistry(): void {
	commandMetaMap.clear();
	executeMap.clear();
}
